import React, { useCallback, useMemo, useState } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { ResponsiveBackgroundContainerBanner } from '@/organisms/WatchProjectHero/ResponsiveBackgroundContainer'
import { DiscoverItemInteraction, useContentInteraction } from '@/services/Analytics/ContentInteractions'
import { findTitleProjectSlug, formatTitleLikeLandscapeImagePath, formatWatchPath } from '@/services/ContentCatalog'
import { CatalogTitleForMap, LinksViewProps, LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { logger } from '@/utils/logging'
import { BannerCarouselDisplay } from './BannerCarouselDisplay'

export function Banner(props: LinksViewProps) {
  const [currentBanner, setCurrentBanner] = useState<LinkViewModel>(props.links?.[0] || ({} as LinkViewModel))
  const { trackInteraction } = useContentInteraction()

  const createInteraction = useCallback((banner: LinkViewModel, itemIndex: number) => {
    const contentId = banner.id ?? ''
    // Map projectType to __typename for content interaction
    const typename =
      banner.projectType === 'series'
        ? 'ContentSeries'
        : banner.projectType === 'movie'
        ? 'ContentMovie'
        : 'ContentSpecial'
    const imageUrl = banner.landscapeNonTitleImage

    if (!contentId || !imageUrl) return null

    return new DiscoverItemInteraction({
      id: contentId,
      __typename: typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial',
      attribution: (banner as unknown as { attribution: unknown })?.attribution,
    })
      .withSurface('discover')
      .withPlacement('hero_carousel', 0, itemIndex, 'carousel')
      .withImageArtwork(imageUrl, 'landscape_display')
  }, [])

  const responsiveBackgroundProps = useMemo(() => {
    const banner = currentBanner
    if (!banner) return {}

    return {
      cloudinaryImgPath: banner?.landscapeNonTitleImage,
      cloudinaryTabletImgPath: banner?.landscapeNonTitleImage,
      cloudinaryMobileImgPath: banner?.squareNonTitleImage,
    }
  }, [currentBanner])

  return (
    <LazyHydrate whenVisible>
      <div className="w-full">
        <ResponsiveBackgroundContainerBanner
          cloudinaryImgPath={responsiveBackgroundProps.cloudinaryImgPath ?? ''}
          cloudinaryTabletImgPath={responsiveBackgroundProps.cloudinaryTabletImgPath ?? ''}
          cloudinaryMobileImgPath={responsiveBackgroundProps.cloudinaryMobileImgPath ?? ''}
          theaterBackgroundPosition="bg-bottom"
        />
        <div className="">
          <BannerCarouselDisplay
            heroBanners={props.links}
            currentBanner={currentBanner}
            setCurrentBanner={setCurrentBanner}
            createInteraction={createInteraction}
            trackInteraction={trackInteraction}
          />
        </div>
      </div>
    </LazyHydrate>
  )
}

Banner.mapTitle = mapToLink

export function mapToLink(title: CatalogTitleForMap): LinkViewModel | undefined {
  const path = formatTitleLikeLandscapeImagePath(title)
  const imageUrl = path && getCloudinaryImageUrl({ path })
  const linkUrl = formatWatchPath(title)
  if (!imageUrl || !linkUrl) {
    logger().error(
      `A link view model cannot be created for this title because one or both of imageUrl and linkUrl could not be generated.`,
      { component: Banner.name, title, imageUrl, linkUrl },
    )
    return
  }

  return {
    name: title.title,
    alt: title.title,
    label: title.description.short,
    imageUrl,
    linkUrl,
    isInEarlyAccess: 'isInEarlyAccess' in title ? Boolean(title.isInEarlyAccess) : false,
    track: {
      eventName: 'Project Tile Clicked',
      payload: {
        name: title.title,
        slug: findTitleProjectSlug(title),
        label: title.description.short,
        tileType: 'landscape_rail',
        imageUrl,
        linkUrl,
      },
    },
  }
}
